import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { DashboardOrderSummary, OrderStatus } from '../../types';
import { getDashboardOrderColor, getOrderStatusLabel, normalizeOrderStatus } from '../../utils';
import { DateUtils } from '../../utils/date-utils';
import { showToast } from '../../utils/toast-service';
import { useUpdateOrderDueDateMutation } from '../order/orders-api-slice';
import { getAllStatusIcons } from './order-status-icons';

interface OrderDetailPopupProps {
  open: boolean;
  order: DashboardOrderSummary;
  onClose: () => void;
  canReschedule?: boolean;
  onRefresh?: () => void;
}

export const OrderDetailPopup = ({
  open,
  order,
  onClose,
  canReschedule = false,
  onRefresh,
}: OrderDetailPopupProps) => {
  const backgroundColor = getDashboardOrderColor(order.colorCode);
  const normalizedStatus = normalizeOrderStatus(order.status);
  // Don't show "Past Due" for Complete, Ready for Pickup, or Shipped orders
  const shouldShowPastDue =
    order.isPastDue &&
    normalizedStatus !== OrderStatus.COMPLETE &&
    normalizedStatus !== OrderStatus.READY_FOR_PICKUP &&
    normalizedStatus !== OrderStatus.SHIPPED;
  const statusIcons = getAllStatusIcons();

  const [isEditingDueDate, setIsEditingDueDate] = useState(false);
  const [dueDateValue, setDueDateValue] = useState(
    order.dueDate ? DateUtils.toDateInputFormat(order.dueDate) : ''
  );
  const [localOrder, setLocalOrder] = useState(order);
  const [updateDueDate, { isLoading: isUpdating }] = useUpdateOrderDueDateMutation();

  // Update local order when prop changes
  useEffect(() => {
    setLocalOrder(order);
    setDueDateValue(order.dueDate ? DateUtils.toDateInputFormat(order.dueDate) : '');
  }, [order]);

  const handleEditClick = () => {
    // Open in a new tab
    window.open(`/order-details/${order.id}`, '_blank');
  };

  const handleEditDueDateClick = () => {
    setIsEditingDueDate(true);
    setDueDateValue(localOrder.dueDate ? DateUtils.toDateInputFormat(localOrder.dueDate) : '');
  };

  const handleCancelDueDateEdit = () => {
    setIsEditingDueDate(false);
    setDueDateValue(localOrder.dueDate ? DateUtils.toDateInputFormat(localOrder.dueDate) : '');
  };

  const handleSaveDueDate = async () => {
    if (!canReschedule) {
      showToast('You do not have permission to reschedule orders.', 'error');
      return;
    }

    try {
      const isoDate = DateUtils.fromDateInputFormat(dueDateValue);
      if (!isoDate) {
        showToast('Please select a valid date.', 'error');
        return;
      }

      await updateDueDate({
        orderId: localOrder.id,
        request: { newDueDate: isoDate },
      }).unwrap();

      // Update local order state immediately
      setLocalOrder({
        ...localOrder,
        dueDate: isoDate,
      });
      setIsEditingDueDate(false);
      showToast('Order due date updated successfully.', 'success');

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to update due date:', error);
      showToast('Failed to update order due date. Please try again.', 'error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          color: '#FFFFFF',
          position: 'relative',
          pr: 4,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="div">
              Order Details - {order.orderNumber}
            </Typography>
            <Tooltip title="Edit Order">
              <IconButton onClick={handleEditClick} sx={{ color: '#FFFFFF' }} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            {getOrderStatusLabel(order.status)}
          </Typography>
          <Typography variant="body2" component="div">
            {order.dealerName}
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={onClose}
            sx={{ color: '#FFFFFF', position: 'absolute', top: 8, right: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              PO#:
            </Typography>
            <Typography variant="body1">{order.po}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Order Date:
            </Typography>
            <Typography variant="body1">
              {DateUtils.toDisplayDateTime(order.orderDate)}
              {shouldShowPastDue && (
                <Chip label="Past Due" color="error" size="small" sx={{ ml: 1 }} />
              )}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Order Due Date:
            </Typography>
            {isEditingDueDate ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  value={dueDateValue}
                  onChange={e => setDueDateValue(e.target.value)}
                  size="small"
                  disabled={isUpdating}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ flex: 1, minWidth: 150 }}
                />
                <Tooltip title="Save">
                  <IconButton
                    size="small"
                    onClick={handleSaveDueDate}
                    disabled={isUpdating}
                    color="primary"
                    sx={{ p: 0.5 }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={handleCancelDueDateEdit}
                    disabled={isUpdating}
                    sx={{ p: 0.5 }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1">
                  {localOrder.dueDate ? DateUtils.toDisplayDate(localOrder.dueDate) : 'N/A'}
                </Typography>
                {canReschedule && (
                  <Tooltip title="Edit due date">
                    <IconButton
                      size="small"
                      onClick={handleEditDueDateClick}
                      sx={{ p: 0.25, ml: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

          {order.completionDate && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Completion Date:
              </Typography>
              <Typography variant="body1">
                {DateUtils.toDisplayDateTime(order.completionDate)}
              </Typography>
            </Box>
          )}

          {/* Order Status Icons */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Order Status:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {statusIcons.map(({ status, icon, label }) => {
                // Compare as numbers to ensure proper matching
                const isActive = Number(status) === Number(normalizedStatus);
                return (
                  <Chip
                    key={status}
                    label={`${icon} ${label}`}
                    variant="outlined"
                    size="small"
                    disabled={!isActive}
                    sx={{
                      opacity: isActive ? 1 : 0.5,
                      cursor: isActive ? 'default' : 'not-allowed',
                      // Active chips: white/light background with dark border and text
                      ...(isActive && {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderColor: '#1976d2',
                        borderWidth: 2,
                        color: '#000000',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          color: '#000000',
                        },
                      }),
                      // Inactive chips: keep default outlined style with reduced opacity
                      ...(!isActive && {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      }),
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
